import { useState, useEffect, useMemo } from 'react';
import { Image, Button, Pagination } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import BigNumber, { toFormatUnit } from '@fe/utils/number';
import { useStakingStore } from '@/store/staking';
import { sliceTableData } from '@/utils/util';
import NoData from '@/components/noData';
import { DEFAULT_VAL } from '@/utils/constants';
import { PoolItem, NetworkItem } from '@/types/staking';
import cls from 'classnames';
import { CardTitle } from './CardTitle';

import styles from './index.module.scss';
import commonStyles from '../index.module.less';

interface IProps {
  network?: string;
  networkList: NetworkItem[];
  poolList: PoolItem[];
  currentTab: string;
  handleStakingClick: (params: { id: number; poolCode: string }) => void;
}

const PAGE_SIZE = 8;

export const RowBox = ({
  network,
  networkList,
  poolList,
  currentTab,
  handleStakingClick,
}: IProps) => {
  const fm = useFm();
  const { poolLoading } = useStakingStore();
  const [currentPage, setCurrentPage] = useState(1);

  // 用于分页的总数据
  const allData = useMemo(() => {
    if (network) {
      return poolList.filter((item) => item.chainCode === network);
    }
    return poolList;
  }, [network, poolList]);

  // 当页展示数据
  const data = useMemo(() => {
    if (network) {
      const formatData = allData.filter((item) => item.chainCode === network);
      return sliceTableData(formatData, currentPage, PAGE_SIZE) ?? [];
    }
    return sliceTableData(allData, currentPage, PAGE_SIZE) ?? [];
  }, [allData, currentPage, network]);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    // tab 或 network 切换时 重置页脚
    setCurrentPage(1);
  }, [currentTab, network]);

  // 暂无数据
  if (!poolLoading && !data?.length) {
    return (
      <div
        className={cls(
          'flex justify-center items-center',
          commonStyles.noDataBg
        )}
      >
        <NoData />
      </div>
    );
  }

  return (
    <div>
      <div
        className={cls('flex flex-wrap pt-6', commonStyles.contentBg)}
        key={currentTab}
      >
        {data.map((item) => {
          const {
            id,
            icon,
            poolName,
            poolType,
            poolCode,
            baseStakeCoins,
            chainCode,
            apy,
            tvl,
            poolExtendDTO,
            noticeConfigDTO,
            poolIcons,
          } = item;
          const networkData = networkList.find(
            (item) => item.chainCode === chainCode
          );

          return (
            <div
              key={id}
              className={cls(styles.box, commonStyles.box)}
              onClick={() =>
                handleStakingClick({
                  id,
                  poolCode,
                })
              }
            >
              <div>
                <CardTitle
                  icon={icon.night}
                  poolName={poolName}
                  poolCode={poolCode}
                  poolType={poolType}
                  chainCode={chainCode}
                  isHot={poolExtendDTO?.isHot}
                  containerClass={commonStyles.infoContainerClass}
                  noticeContent={noticeConfigDTO?.content}
                  baseStakeCoins={baseStakeCoins}
                  poolIcons={poolIcons}
                />
                {poolExtendDTO?.isNew && (
                  <div className={commonStyles.newTag}>New</div>
                )}
                {/* apy */}
                <div className="flex mt-4">
                  <div className={commonStyles.apyBg}>
                    <h5
                      className={cls(
                        commonStyles.overflowOneLine,
                        commonStyles.apyTitle
                      )}
                    >
                      {fm('apy')}
                    </h5>
                    <p className={cls(commonStyles.apy)}>
                      +{`${BigNumber(apy).toFixed(2)}%`}
                    </p>
                  </div>

                  {/* tvl */}
                  <div className={cls('flex-1', commonStyles.tvlBg)}>
                    <h5
                      className={cls(
                        'text-right',
                        commonStyles.overflowOneLine,
                        commonStyles.tvlTitle
                      )}
                    >
                      {fm('tvl')}
                    </h5>
                    <p className={cls('text-right', commonStyles.tvl)}>
                      ${toFormatUnit(tvl, 2)}
                    </p>
                  </div>
                </div>

                {/* earn */}
                <div
                  className={cls(
                    'flex mt-4 justify-between',
                    commonStyles.earnBg
                  )}
                >
                  <h5
                    className={cls(
                      commonStyles.overflowOneLine,
                      commonStyles.earnTitle
                    )}
                  >
                    {fm('earn')}
                  </h5>
                  <p className={cls(commonStyles.earn)}>
                    {poolExtendDTO?.poolLabelDTO?.rewardMsg || DEFAULT_VAL}
                  </p>
                </div>

                {/* network */}
                <div
                  className={cls(
                    'flex mt-2 justify-between',
                    commonStyles.networkBg
                  )}
                >
                  <h5
                    className={cls(
                      commonStyles.overflowOneLine,
                      commonStyles.networkTitle
                    )}
                  >
                    {fm('network')}
                  </h5>
                  {!networkData?.iconNightUrl && DEFAULT_VAL}
                  {networkData?.iconNightUrl && (
                    <div className={cls('flex ', commonStyles.networkImg)}>
                      <Image
                        alt=""
                        className={cls(commonStyles.icon)}
                        src={networkData.iconNightUrl}
                        style={{
                          height: 20,
                          width: 20,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <Button
                size="small"
                variant="secondary"
                className={cls('w-full mt-4', commonStyles.investBtn)}
              >
                {fm('staking')}
              </Button>
            </div>
          );
        })}
      </div>
      {/* 页脚 */}
      {Boolean(data?.length) && (
        <div
          className={cls(
            'w-full flex justify-center items-center',
            commonStyles.paginationContainer
          )}
        >
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            onChange={onPageChange}
            total={allData?.length || 0}
            itemRender={(page, type, element) => {
              if (type === 'page') {
                return (
                  <div
                    className={cls(
                      styles.pageBtn,
                      currentPage === page ? styles.pageBtnActive : ''
                    )}
                  >
                    {page}
                  </div>
                );
              }
              return element;
            }}
          />
        </div>
      )}
    </div>
  );
};
